import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { BYPASS_AUTH } from 'app/core/interceptors/auth.interceptor';
import { BYPASS_ERROR } from 'app/core/interceptors/error.interceptor';
import { IUserTokenPayload } from 'app/core/interfaces/login.interface';
import { IConfirmUserRegistrationRequest } from 'app/core/interfaces/register.interface';
import { IAvailableWorkspace, IDeleteUserRequest, IRegisterUserOperatorPlusResult, IUnlinkUserWorkspacesRequest, IUpdateRole, IUpdateStatusUser, IUserCountsByStatus, IUserDetailsResponse, IUserInvite, IUserInviteResponse } from 'app/core/interfaces/user.interface';
import { IPaginatedResponse } from 'app/shared/interfaces/IPagination';
import { catchError, map, Observable, tap, throwError } from 'rxjs';

const baseUrl = environment.apiHost;
@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private readonly http = inject(HttpClient);

  getAllUsers(
    page: number = 1,
    size: number = 3,
    query?: string,
  ): Observable<{ items: IUserInviteResponse[]; total: number }> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))

    const q = query?.trim();
    if (q) params = params.set('query', q);

    return this.http
      .get<IPaginatedResponse<IUserInviteResponse> | IUserInviteResponse[]>(
        `${baseUrl}/User/list`,
        { params }
      )
      .pipe(
        map((resp: any) => {
          if (Array.isArray(resp)) {
            return { items: resp as IUserInviteResponse[], total: resp.length };
          }

          const items: IUserInviteResponse[] = resp?.items ?? [];
          const total: number = (
            resp?.totalItems ?? 
            resp?.total ?? 
            resp?.totalCount ?? 
            items.length
          );
          return { items, total };
        })
      );
  }

  confirmValidToken(token: string): Observable<IUserTokenPayload> {
    const params = new HttpParams().set('token', token);
    const context = new HttpContext()
      .set(BYPASS_AUTH, true)
      .set(BYPASS_ERROR, true);
    return this.http.get<IUserTokenPayload>(`${baseUrl}/User/verifyToken`, { params, context });
  }

  confirmUserRegistration(payload: IConfirmUserRegistrationRequest): Observable<boolean> {
    return this.http.post<boolean>(`${baseUrl}/User/confirmUser`, payload);
  }

  postRegisterUserOperatorPlus(
    items: IUserInvite[],
    workspaceId: string
  ): Observable<IRegisterUserOperatorPlusResult[]> {
    const data = {
      ownerID: workspaceId,
      userInfo: items.map(it => ({
        email: it.email,
        role: Number(it.rol?.id)
      }))
    };

    return this.http
      .post<IRegisterUserOperatorPlusResult[]>(
        `${baseUrl}/User/registerUserOperatorPlus`,
        data
      )
      .pipe(
        tap(resp => console.log('Respuesta del backend:', resp))
      );
  }

  cancelInvitation (email: string): Observable<boolean> {
    const data = {
      email: email
    };
    return this.http.patch(`${baseUrl}/User/cancelInvitation `, data)
      .pipe(
        map(() => true)
      );
  }

  Invitation (email: string): Observable<boolean> {
    const data = {
      email: email
    };
    return this.http.patch(`${baseUrl}/User/cancelInvitation `, data)
      .pipe(
        map(() => true)
      );
  }

  deleteInvitation(email: string): Observable<boolean> {
    const data = {
      email: email
    };
    return this.http.patch(`${baseUrl}/User/deleteInvitation`, data)
      .pipe(
        map(() => true)
      );
  }

  resendInvitation(email: string): Observable<boolean> {
    const data = { email };

    return this.http.post(`${baseUrl}/User/resendInvitation`, data).pipe(
      tap(resp => {
      }),
      map(() => true),
      catchError(err => {
        return throwError(() => err); 
      })
    );
  }

  updateRole(payload: IUpdateRole) {
    return this.http.patch(`${baseUrl}/User/updateRole`, payload)
  }

  getByIdUser(userId: string): Observable<IUserDetailsResponse> {
    let params = new HttpParams().set('ID', userId);
    return this.http.get<IUserDetailsResponse>(`${baseUrl}/User/details`, { params });
  }

  getAvailableWorkspaces(
    Id: string,
    page: number = 1,
    size: number = 5,
    query?: string
  ): Observable<{ items: IAvailableWorkspace[]; total: number }> {
    let params = new HttpParams()
      .set('ID', String(Id))
      .set('page', String(page))
      .set('size', String(size));

    const q = query?.trim();
    if (q) params = params.set('query', q);

    return this.http
      .get<IPaginatedResponse<IAvailableWorkspace> | IAvailableWorkspace[]>(
        `${baseUrl}/User/availableWorkspaces`,
        { params }
      )
      .pipe(
        map((resp: any) => {
          if (Array.isArray(resp)) {
            return { items: resp as IAvailableWorkspace[], total: resp.length };
          }

          const items: IAvailableWorkspace[] = resp?.items ?? [];
          const total: number = (
            resp?.totalItems ?? 
            resp?.total ?? 
            resp?.totalCount ?? 
            items.length
          );
          return { items, total };
        })
      );
  }

  unLinkUsersWorkspaces(payload: IUnlinkUserWorkspacesRequest): Observable<boolean> {
    return this.http.post<boolean>(`${baseUrl}/User/unlinkWorkspace`, payload);
  }

  deleteUser(payload: IDeleteUserRequest): Observable<any> {
    return this.http.post(`${baseUrl}/User/deleteUser`, payload);
  }

  updateStatusUser(payload: IUpdateStatusUser): Observable<any> {
    return this.http.post(`${baseUrl}/User/updateStatusUser`, payload);
  }

  getUserCountsByStatus(): Observable<IUserCountsByStatus> {
    return this.http.get<IUserCountsByStatus>(`${baseUrl}/User/counts-by-status`);
  }
}
